/** Azure AD B2C sign-in for the SPA — OpenID Connect Authorization Code + PKCE.
 *
 * Implemented directly against the B2C endpoints (no SDK): the browser holds no
 * client secret, the code is bound to a PKCE verifier, and tokens are refreshed
 * silently with the refresh token issued via `offline_access`.
 *
 * Configure with VITE_B2C_* (see .env.example). When unset, `configured` is
 * false and the UI explains that sign-in isn't set up rather than breaking.
 */

const env = import.meta.env;
const API_BASE = (env.VITE_API_BASE_URL ?? "/api").replace(/\/$/, "");

export interface B2CConfig {
  configured: boolean;
  tenant: string;
  policy: string;
  clientId: string;
  apiScope: string;
  authorityDomain: string;
  metadataUrl: string;
}

/** Build-time overrides; otherwise the API supplies these at runtime so a
 *  config change needs only an API restart, not a frontend rebuild. */
const buildTime: Partial<B2CConfig> = {
  tenant: env.VITE_B2C_TENANT,
  policy: env.VITE_B2C_POLICY,
  clientId: env.VITE_B2C_SPA_CLIENT_ID,
  apiScope: env.VITE_B2C_API_SCOPE,
  authorityDomain: env.VITE_B2C_AUTHORITY_DOMAIN,
  metadataUrl: env.VITE_B2C_METADATA_URL,
};

let cfgPromise: Promise<B2CConfig> | null = null;

export function loadConfig(): Promise<B2CConfig> {
  if (cfgPromise) return cfgPromise;
  cfgPromise = (async () => {
    let remote: Partial<B2CConfig> = {};
    try {
      const res = await fetch(`${API_BASE}/auth/config`);
      if (res.ok) {
        const d = await res.json();
        remote = {
          tenant: d.tenant,
          policy: d.policy,
          clientId: d.spa_client_id,
          apiScope: d.api_scope,
          authorityDomain: d.authority_domain,
          metadataUrl: d.metadata_url,
        };
      }
    } catch {
      // API unreachable — fall back to build-time values (if any).
    }
    const merged: B2CConfig = {
      tenant: buildTime.tenant || remote.tenant || "",
      policy: buildTime.policy || remote.policy || "",
      clientId: buildTime.clientId || remote.clientId || "",
      apiScope: buildTime.apiScope || remote.apiScope || "",
      authorityDomain: buildTime.authorityDomain || remote.authorityDomain || "",
      metadataUrl: buildTime.metadataUrl || remote.metadataUrl || "",
      configured: false,
    };
    merged.configured =
      !!merged.clientId &&
      !!merged.apiScope &&
      (!!merged.metadataUrl || (!!merged.tenant && !!merged.policy));
    return merged;
  })();
  return cfgPromise;
}

const REDIRECT_URI = `${window.location.origin}/auth/callback`;

function metadataUrl(c: B2CConfig): string {
  if (c.metadataUrl) return c.metadataUrl;
  const domain = c.authorityDomain || `${c.tenant}.b2clogin.com`;
  return (
    `https://${domain}/${c.tenant}.onmicrosoft.com/` +
    `${c.policy}/v2.0/.well-known/openid-configuration`
  );
}

function scopesFor(c: B2CConfig): string {
  return ["openid", "offline_access", c.apiScope].filter(Boolean).join(" ");
}

const K_TOKENS = "wise.b2c.tokens";
const K_VERIFIER = "wise.b2c.verifier";
const K_STATE = "wise.b2c.state";
const K_RETURN = "wise.b2c.return";

interface Tokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_at: number; // epoch ms
}

interface Metadata {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint: string;
}

let metaCache: Metadata | null = null;

async function metadata(c: B2CConfig): Promise<Metadata> {
  if (metaCache) return metaCache;
  const res = await fetch(metadataUrl(c));
  if (!res.ok) throw new Error(`B2C metadata unavailable (HTTP ${res.status})`);
  metaCache = (await res.json()) as Metadata;
  return metaCache;
}

// ---- PKCE helpers ----

function base64url(bytes: Uint8Array): string {
  let s = "";
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(bytes = 48): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return base64url(a);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

// ---- token storage ----

function readTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(K_TOKENS);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function writeTokens(t: Tokens): void {
  localStorage.setItem(K_TOKENS, JSON.stringify(t));
}

export function clearSession(): void {
  localStorage.removeItem(K_TOKENS);
  sessionStorage.removeItem(K_VERIFIER);
  sessionStorage.removeItem(K_STATE);
}

export function isSignedIn(): boolean {
  return readTokens() !== null;
}

/** Claims from the id_token (display only — the API verifies tokens itself). */
export function idClaims(): Record<string, unknown> | null {
  const t = readTokens();
  if (!t?.id_token) return null;
  try {
    const [, payload] = t.id_token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

// ---- flow ----

/** Redirect the browser to B2C to sign in (or sign up — same user flow).
 *
 * `loginHint` is the email captured on our own sign-in screen; it is passed
 * through as the OIDC `login_hint` so the identity provider's page arrives
 * pre-filled and the user only supplies their credential.
 */
export async function login(
  returnTo = window.location.pathname,
  loginHint?: string,
): Promise<void> {
  const c = await loadConfig();
  if (!c.configured) throw new Error("sign-in is not configured");
  const verifier = randomString();
  const state = randomString(16);
  sessionStorage.setItem(K_VERIFIER, verifier);
  sessionStorage.setItem(K_STATE, state);
  sessionStorage.setItem(K_RETURN, returnTo);

  const { authorization_endpoint } = await metadata(c);
  const url = new URL(authorization_endpoint);
  url.searchParams.set("client_id", c.clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", REDIRECT_URI);
  url.searchParams.set("response_mode", "query");
  url.searchParams.set("scope", scopesFor(c));
  url.searchParams.set("state", state);
  url.searchParams.set("code_challenge", await challengeFor(verifier));
  url.searchParams.set("code_challenge_method", "S256");
  if (loginHint) url.searchParams.set("login_hint", loginHint.trim());
  window.location.assign(url.toString());
}

async function exchange(body: Record<string, string>): Promise<Tokens> {
  const c = await loadConfig();
  const { token_endpoint } = await metadata(c);
  const res = await fetch(token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: c.clientId, scope: scopesFor(c), ...body }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error_description || data.error || `token request failed (${res.status})`);
  }
  const tokens: Tokens = {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id_token: data.id_token,
    expires_at: Date.now() + Number(data.expires_in ?? 3600) * 1000,
  };
  writeTokens(tokens);
  return tokens;
}

/** Complete the redirect: swap ?code= for tokens. Returns where to navigate. */
export async function completeLogin(): Promise<string> {
  const params = new URLSearchParams(window.location.search);
  const error = params.get("error_description") || params.get("error");
  if (error) {
    clearSession();
    throw new Error(error);
  }
  const code = params.get("code");
  const state = params.get("state");
  const verifier = sessionStorage.getItem(K_VERIFIER);
  if (!code || !verifier || state !== sessionStorage.getItem(K_STATE)) {
    throw new Error("sign-in response was invalid or expired");
  }
  await exchange({
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });
  sessionStorage.removeItem(K_VERIFIER);
  sessionStorage.removeItem(K_STATE);
  const back = sessionStorage.getItem(K_RETURN) || "/app";
  sessionStorage.removeItem(K_RETURN);
  return back.startsWith("/auth") ? "/app" : back;
}

/** A valid access token for the API, refreshing silently when near expiry. */
export async function getAccessToken(): Promise<string | null> {
  const tokens = readTokens();
  if (!tokens) return null;
  if (Date.now() < tokens.expires_at - 60_000) return tokens.access_token;
  if (!tokens.refresh_token) {
    clearSession();
    return null;
  }
  try {
    const fresh = await exchange({
      grant_type: "refresh_token",
      refresh_token: tokens.refresh_token,
    });
    return fresh.access_token;
  } catch {
    clearSession(); // refresh token expired/revoked — the user signs in again
    return null;
  }
}

/** Sign out locally and at B2C. */
export async function logout(): Promise<void> {
  clearSession();
  const c = await loadConfig();
  if (!c.configured) {
    window.location.assign("/");
    return;
  }
  try {
    const { end_session_endpoint } = await metadata(c);
    const url = new URL(end_session_endpoint);
    url.searchParams.set("post_logout_redirect_uri", window.location.origin);
    window.location.assign(url.toString());
  } catch {
    window.location.assign("/");
  }
}
